import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useSWR } from 'modules/network/hooks/useSwr'
import { createContext, ReactNode, useCallback } from 'react'
import { PageLoader } from 'shared/ui/layout/PageLoader'
import {
  assetERCContracts,
  DEFAULT_DECIMALS,
  PoolAsset,
} from '../constants/poolAssets'

type DecimalsMap = Record<string, number | undefined>

type ContextValue = {
  getAssetDecimals: (asset: string | undefined) => number
}

export const AssetsStateContext = createContext<ContextValue>({
  getAssetDecimals: () => DEFAULT_DECIMALS,
})

type Props = {
  children?: ReactNode
}

export const AssetsStateProvider = ({ children }: Props) => {
  const { chainId } = useWeb3()

  const { data: assetsState, isLoading } = useSWR(
    `${chainId}-assets-data`,
    async () => {
      const requestsAssets = Object.keys(assetERCContracts).map(
        async (asset: PoolAsset) => {
          const connected = assetERCContracts[asset].connectRpc({ chainId })
          const decimals =
            (await connected.decimals().catch(console.error)) ||
            DEFAULT_DECIMALS
          return { asset, decimals }
        },
      )

      const responses = await Promise.all(requestsAssets)

      return responses.reduce((result, assetData) => {
        result[assetData.asset] = assetData.decimals
        return result
      }, {} as DecimalsMap)
    },
  )

  const getAssetDecimals = useCallback(
    (asset: string | undefined) => {
      if (!assetsState || !asset) {
        return DEFAULT_DECIMALS
      }
      const decimals = assetsState[asset]
      return decimals ?? DEFAULT_DECIMALS
    },
    [assetsState],
  )

  if (!assetsState || isLoading) {
    return <PageLoader />
  }

  return (
    <AssetsStateContext.Provider value={{ getAssetDecimals }}>
      {children}
    </AssetsStateContext.Provider>
  )
}
